/**
 * Employee self-profile page.
 *
 * Pulls the 5 fields tracked by `compute_employees_profile_pct`
 * (national_id / birth_date / phone / sso_number / bank +
 * bank_account_masked) plus a few read-only display fields, lets the
 * employee fill them, PATCHes back to /me/profile.
 *
 * Linked from the Day-7 self-reminder Flex bubble:
 *   liff.line.me/<id>/profile
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Save, CheckCircle2, AlertTriangle, ArrowLeft, Upload, Image as ImageIcon, Loader2 } from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { Loading } from '../components/layout/Loading';
import {
  useProfile,
  useUpdateProfile,
  useOwnEducation,
  useUpsertOwnEducation,
  useDeleteOwnEducation,
  useOwnWorkHistory,
  useUpsertOwnWorkHistory,
  useDeleteOwnWorkHistory,
  useOwnDocuments,
  useCreateOwnDocument,
  useDeleteOwnDocument,
} from '../hooks';
import { uploadFile, resolveFileUrl } from '../lib/api-client';
import { EducationSection, WorkHistorySection, DocumentsSection } from '../components/domain/ProfileExtras';
import { useToast } from '../components/ui/toast';

interface FormData {
  nickname: string;
  national_id: string;
  birth_date: string;
  phone: string;
  sso_number: string;
  bank: string;
  bank_account_masked: string;
  id_copy_path: string;
  bankbook_path: string;
  address_current: string;
  address_registered: string;
  emergency_name: string;
  emergency_relationship: string;
  emergency_phone: string;
}

export function Profile() {
  const { data, isLoading } = useProfile();
  const update = useUpdateProfile();
  const eduQuery = useOwnEducation();
  const eduUpsert = useUpsertOwnEducation();
  const eduDelete = useDeleteOwnEducation();
  const workQuery = useOwnWorkHistory();
  const workUpsert = useUpsertOwnWorkHistory();
  const workDelete = useDeleteOwnWorkHistory();
  const docsQuery = useOwnDocuments();
  const docsCreate = useCreateOwnDocument();
  const docsDelete = useDeleteOwnDocument();
  const toast = useToast();
  const navigate = useNavigate();
  const [idCopyPreview, setIdCopyPreview] = useState<string | null>(null);
  const [bankbookPreview, setBankbookPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState<'id' | 'bank' | null>(null);
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      nickname: '',
      national_id: '',
      birth_date: '',
      phone: '',
      sso_number: '',
      bank: '',
      bank_account_masked: '',
      id_copy_path: '',
      bankbook_path: '',
      address_current: '',
      address_registered: '',
      emergency_name: '',
      emergency_relationship: '',
      emergency_phone: '',
    },
  });

  // Sync defaults from server response
  useEffect(() => {
    if (!data) return;
    const p = data.profile as Record<string, string | null>;
    reset({
      nickname: p.nickname ?? '',
      national_id: p.national_id ?? '',
      birth_date: p.birth_date ?? '',
      phone: p.phone ?? '',
      sso_number: p.sso_number ?? '',
      bank: p.bank ?? '',
      bank_account_masked: p.bank_account_masked ?? '',
      id_copy_path: p.id_copy_path ?? '',
      bankbook_path: p.bankbook_path ?? '',
      address_current: p.address_current ?? '',
      address_registered: p.address_registered ?? '',
      emergency_name: p.emergency_name ?? '',
      emergency_relationship: p.emergency_relationship ?? '',
      emergency_phone: p.emergency_phone ?? '',
    });
    setIdCopyPreview(resolveFileUrl(p.id_copy_url));
    setBankbookPreview(resolveFileUrl(p.bankbook_url));
  }, [data, reset]);

  async function handleFileChoose(kind: 'id' | 'bank', file: File | null) {
    if (!file) return;
    setUploading(kind);
    try {
      const path = await uploadFile(file, 'identity', file.name);
      if (kind === 'id') {
        setValue('id_copy_path', path, { shouldDirty: true });
        setIdCopyPreview(URL.createObjectURL(file));
      } else {
        setValue('bankbook_path', path, { shouldDirty: true });
        setBankbookPreview(URL.createObjectURL(file));
      }
    } catch (e) {
      toast.error(`อัปโหลดไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`);
    } finally {
      setUploading(null);
    }
  }

  if (isLoading || !data) {
    return (
      <PageLayout title="กรอกประวัติพนักงาน">
        <Loading />
      </PageLayout>
    );
  }

  const pct = data.completion_pct;
  const barColor =
    pct < 30 ? '#b4564f' : pct < 70 ? '#9c7d3a' : '#5b7a4f';

  async function onSubmit(form: FormData) {
    // เลขบัตร: ผู้ใช้พิมพ์เว้นวรรค/ขีดกลางได้ — ตัดเหลือตัวเลขล้วนตอนยืนยัน
    const cleaned: FormData = { ...form };
    if (typeof cleaned.national_id === 'string') {
      cleaned.national_id = cleaned.national_id.replace(/\D/g, '');
      if (cleaned.national_id && cleaned.national_id.length !== 13) {
        toast.error('เลขบัตรประชาชนต้องมี 13 หลัก');
        return;
      }
    }
    try {
      await update.mutateAsync({ ...cleaned } as Record<string, string | undefined>);
      toast.success('บันทึกประวัติแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  return (
    <PageLayout title="กรอกประวัติพนักงาน">
      {/* Identity strip */}
      <div className="card p-4 mb-4">
        <div className="text-xs text-gray-500">รหัสพนักงาน</div>
        <div className="font-semibold">
          {data.profile.first_name} {data.profile.last_name}
          {data.profile.nickname ? ` (${data.profile.nickname})` : ''}
          {' · '}
          <span className="text-gray-500 text-sm">{data.profile.emp_code}</span>
        </div>
        <div className="text-sm text-gray-500 mt-0.5">
          {data.profile.department} · {data.profile.position}
        </div>
      </div>

      {/* Progress card */}
      <div className="card p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold">ความสมบูรณ์ของประวัติ</div>
          <div className="font-bold text-lg" style={{ color: barColor }}>
            {pct}%
          </div>
        </div>
        <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${Math.max(pct, 3)}%`, backgroundColor: barColor }}
          />
        </div>
        {pct === 100 ? (
          <div className="flex items-center gap-1 text-sm text-green-700 mt-3">
            <CheckCircle2 className="w-4 h-4" />
            ครบ 100% แล้ว — ขอบคุณค่ะ
          </div>
        ) : data.missing_sections.length > 0 ? (
          <div className="mt-3">
            <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> ยังขาด {data.missing_sections.length} ส่วน:
            </div>
            <div className="flex flex-wrap gap-1">
              {data.missing_sections.map((s) => (
                <span key={s} className="badge bg-amber-100 text-amber-800">
                  {s}
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="card p-4 space-y-4">
        <div>
          <label className="label">ชื่อเล่น</label>
          <input
            type="text"
            maxLength={50}
            placeholder="เช่น ปู, ฟาง"
            className="input"
            {...register('nickname')}
          />
        </div>

        <div>
          <label className="label">เลขประจำตัวประชาชน (13 หลัก)</label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={20}
            placeholder="1234567890123 (เว้นวรรค/ขีดกลางได้)"
            className="input"
            {...register('national_id')}
          />
          {errors.national_id && (
            <div className="text-xs text-red-600 mt-1">{errors.national_id.message}</div>
          )}
        </div>

        <div>
          <label className="label">วันเกิด</label>
          <input type="date" className="input" {...register('birth_date')} />
        </div>

        <div>
          <label className="label">เบอร์โทรศัพท์</label>
          <input
            type="tel"
            inputMode="tel"
            placeholder="0812345678"
            className="input"
            {...register('phone', { maxLength: 20 })}
          />
        </div>

        <div>
          <label className="label">เลขประกันสังคม</label>
          <input
            type="text"
            placeholder="ตัวเลขประกันสังคมของคุณ"
            className="input"
            {...register('sso_number', { maxLength: 20 })}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">ธนาคาร</label>
            <select className="input" {...register('bank')}>
              <option value="">— เลือก —</option>
              <option value="SCB">ไทยพาณิชย์ (SCB)</option>
              <option value="KBANK">กสิกรไทย (K)</option>
              <option value="BBL">กรุงเทพ (BBL)</option>
              <option value="KTB">กรุงไทย (KTB)</option>
              <option value="TTB">ทหารไทยธนชาต (TTB)</option>
              <option value="BAY">กรุงศรี (BAY)</option>
              <option value="GSB">ออมสิน</option>
              <option value="BAAC">ธ.ก.ส.</option>
              <option value="OTHER">อื่นๆ</option>
            </select>
          </div>
          <div>
            <label className="label">เลขที่บัญชี (4 หลักท้าย)</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              placeholder="1234"
              className="input"
              {...register('bank_account_masked', {
                pattern: { value: /^\d{0,4}$/, message: 'กรอก 4 หลักท้าย' },
              })}
            />
          </div>
        </div>

        {/* === Documents === */}
        <div className="pt-2">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
            เอกสารแนบ
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FileTile
              title="สำเนาบัตร ปชช."
              preview={idCopyPreview}
              uploading={uploading === 'id'}
              onChoose={(f) => handleFileChoose('id', f)}
            />
            <FileTile
              title="หน้าสมุดบัญชี"
              preview={bankbookPreview}
              uploading={uploading === 'bank'}
              onChoose={(f) => handleFileChoose('bank', f)}
            />
          </div>
        </div>

        {/* === Address === */}
        <div className="pt-2">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
            ที่อยู่
          </div>
          <div className="space-y-3">
            <div>
              <label className="label">ที่อยู่ปัจจุบัน *</label>
              <textarea
                rows={2}
                placeholder="บ้านเลขที่ ถนน แขวง/ตำบล เขต/อำเภอ จังหวัด รหัสไปรษณีย์"
                className="input resize-none"
                {...register('address_current', { maxLength: 500 })}
              />
            </div>
            <div>
              <label className="label">ที่อยู่ตามทะเบียนบ้าน (ถ้าต่างจากปัจจุบัน)</label>
              <textarea
                rows={2}
                placeholder="ปล่อยว่างถ้าเหมือนที่อยู่ปัจจุบัน"
                className="input resize-none"
                {...register('address_registered', { maxLength: 500 })}
              />
            </div>
          </div>
        </div>

        {/* === Emergency Contact === */}
        <div className="pt-2">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
            ผู้ติดต่อฉุกเฉิน
          </div>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">ชื่อ-นามสกุล</label>
                <input className="input" placeholder="สมศรี ใจดี" {...register('emergency_name', { maxLength: 100 })} />
              </div>
              <div>
                <label className="label">ความสัมพันธ์</label>
                <select className="input" {...register('emergency_relationship')}>
                  <option value="">— เลือก —</option>
                  <option value="แม่">แม่</option>
                  <option value="พ่อ">พ่อ</option>
                  <option value="คู่สมรส">คู่สมรส</option>
                  <option value="พี่/น้อง">พี่/น้อง</option>
                  <option value="ลูก">ลูก</option>
                  <option value="ญาติ">ญาติ</option>
                  <option value="เพื่อน">เพื่อน</option>
                </select>
              </div>
            </div>
            <div>
              <label className="label">เบอร์โทรฉุกเฉิน</label>
              <input
                type="tel"
                inputMode="tel"
                placeholder="0812345678"
                className="input"
                {...register('emergency_phone', { maxLength: 20 })}
              />
            </div>
          </div>
        </div>

        {/* === Education === */}
        <div className="pt-2">
          <EducationSection query={eduQuery} upsert={eduUpsert} remove={eduDelete} />
        </div>

        {/* === Work history === */}
        <div className="pt-2">
          <WorkHistorySection query={workQuery} upsert={workUpsert} remove={workDelete} />
        </div>

        {/* === Documents (N attachments) === */}
        <div className="pt-2">
          <DocumentsSection query={docsQuery} create={docsCreate} remove={docsDelete} />
        </div>

        <div className="flex gap-2 sticky bottom-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-secondary"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button
            type="submit"
            disabled={update.isPending}
            className="btn-primary flex-1"
          >
            <Save className="w-5 h-5" />
            {update.isPending
              ? 'กำลังบันทึก…'
              : update.isSuccess
                ? 'บันทึกแล้ว ✓'
                : 'บันทึก'}
          </button>
        </div>
      </form>
    </PageLayout>
  );
}

function FileTile({
  title,
  preview,
  uploading,
  onChoose,
}: {
  title: string;
  preview: string | null;
  uploading: boolean;
  onChoose: (f: File | null) => void;
}) {
  return (
    <label className="block border-2 border-dashed border-gray-300 rounded-lg p-2 cursor-pointer hover:bg-gray-50 transition relative aspect-[4/3] overflow-hidden">
      <input
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => onChoose(e.target.files?.[0] ?? null)}
      />
      {preview ? (
        <img src={preview} alt={title} className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
          <ImageIcon className="w-8 h-8 mb-1" />
          <span className="text-xs">{title}</span>
        </div>
      )}
      {uploading && (
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-white animate-spin" />
        </div>
      )}
      <div className="absolute bottom-1 right-1 bg-white/90 rounded-full p-1 shadow">
        <Upload className="w-3.5 h-3.5 text-brand-600" />
      </div>
    </label>
  );
}
